<script setup>
    import MainHeader from './../../components/HeaderComponent.vue';
    import { getCurrentInstance } from 'vue';
    import { useRouter } from 'vue-router';
    import { reactive, inject } from 'vue';
    //import { ref } from 'vue';
    import { useSigninResetStore } from '@/stores/signin/signinResetStore';
    import { signinResetPasswordFormInfo } from '@/models/signinModel';
    import BaseInput from './../../components/BaseInputComponent.vue';
    import PasswordInput from './../../components/PasswordInputComponent.vue';
    import Utils from '../../commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    import Tooltip from '@/components/TooltipComponent.vue';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    const signinResetPasswordForm = reactive(new signinResetPasswordFormInfo());
    const router = useRouter();
    const loginStore = useLoginStore();
    const signinResetStore = useSigninResetStore();

    // メッセージダイアログ取得
    const { messageDialog } = inject('messageDialogKey');
    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * パスワードリセット新パスワード入力
     */
    async function updateNewPassword() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const requestBody = {
            VendorId: loginStore.getVendorId,
            PwdrstPk: signinResetStore.getPwdrstPk,
            CfmCode: signinResetPasswordForm.authenticationCode,
            UserPwd: signinResetPasswordForm.newPassword,
        };
        // パスワードリセット2APIをコールして、パスワードリセットを完了する。
        const response = await signinResetStore.sendPasswordResetCodeAndNewPasswordAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_P, requestBody);

        // 正常応答の場合
        if (response.success) {
            // Storeを初期化する。
            signinResetStore.reset();

            // パスワードリセット完了画面へ遷移する。
            router.push({ name: 'signinResetComplete' });
        } else {
            // エラーメッセージを表示して以降の処理を実施しない。
            messageDialog('error', response.errorMessage);
            return;
        }
    }
    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 認証コード
        if (Utils.isNullEmpty(signinResetPasswordForm.authenticationCode)) {
            message += i18n.global.t('signinResetPassword.message.message1') + '\n';
        }
        // 新しいパスワード
        if (Utils.isNullEmpty(signinResetPasswordForm.newPassword)) {
            message += i18n.global.t('signinResetPassword.message.message4') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(signinResetPasswordForm.newPassword)) {
                message += i18n.global.t('signinResetPassword.message.message5') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(signinResetPasswordForm.newPassword)) {
                message += i18n.global.t('signinResetPassword.message.message6') + '\n';
            }
        }
        // 新しいパスワード再入力
        if (Utils.isNullEmpty(signinResetPasswordForm.reNewPassword)) {
            message += i18n.global.t('signinResetPassword.message.message7') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(signinResetPasswordForm.reNewPassword)) {
                message += i18n.global.t('signinResetPassword.message.message8') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(signinResetPasswordForm.reNewPassword)) {
                message += i18n.global.t('signinResetPassword.message.message9') + '\n';
            }
        }
        // パスワードの一致
        if (!Utils.isStringMatch(signinResetPasswordForm.newPassword, signinResetPasswordForm.reNewPassword)) {
            message += i18n.global.t('common.message.emptyPasswordUnmatch2') + '\n';
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }
</script>

<template>
    <section id="main" class="bg login extra">
        <MainHeader />
        <div id="contents" class="large">
            <h2>{{ $t('signinResetPassword.parts.pageTitle1') }}</h2>
            <h3>{{ $t('signinResetPassword.parts.pageInformation1') }}</h3>
            <section class="form">
                <dl>
                    <dt style="padding-top: 20px">
                        <label>{{ $t('signinResetPassword.parts.dl1') }}</label>
                    </dt>
                    <dd>
                        <BaseInput v-model:input="signinResetPasswordForm.authenticationCode" type="text" />
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <label>{{ $t('signinResetPassword.parts.dl2') }}</label>
                        <div class="statusInfoBox resetPassword">
                            <Tooltip>{{ $t('signinResetPassword.parts.blowoutInformation1') }}</Tooltip>
                        </div>
                    </dt>
                    <dd>
                        <PasswordInput v-model:input="signinResetPasswordForm.newPassword" type="text" />
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <label>{{ $t('signinResetPassword.parts.dl3') }}</label>
                    </dt>
                    <dd>
                        <PasswordInput v-model:input="signinResetPasswordForm.reNewPassword" type="text" />
                    </dd>
                </dl>
            </section>
            <ul class="btnArea">
                <li>
                    <button type="button" class="btnPrimary" id="moveToResetPasswordComplete" @click="updateNewPassword" :disabled="disabledStore.isDisabled">{{ $t('signinResetPassword.parts.button1') }}</button>
                </li>
            </ul>
        </div>
    </section>
</template>

<style></style>
